import { isArray } from 'vega-util';
import { valueExpr, vgField } from './channeldef';
import { fieldExpr as timeUnitFieldExpr, normalizeTimeUnit } from './timeunit';
import { stringify } from './util';
import { isSignalRef } from './vega.schema';
export function isSelectionPredicate(predicate) {
  return predicate === null || predicate === void 0 ? void 0 : predicate['param'];
}
export function isFieldEqualPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && predicate.equal !== undefined;
}
export function isFieldLTPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && predicate.lt !== undefined;
}
export function isFieldLTEPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && predicate.lte !== undefined;
}
export function isFieldGTPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && predicate.gt !== undefined;
}
export function isFieldGTEPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && predicate.gte !== undefined;
}
export function isFieldRangePredicate(predicate) {
  if (predicate === null || predicate === void 0 ? void 0 : predicate.field) {
    if (isArray(predicate.range) && predicate.range.length === 2) {
      return true;
    } else if (isSignalRef(predicate.range)) {
      return true;
    }
  }

  return false;
}
export function isFieldOneOfPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && (isArray(predicate.oneOf) || isArray(predicate.in)) // backward compatibility
  ;
}
export function isFieldValidPredicate(predicate) {
  return !!(predicate === null || predicate === void 0 ? void 0 : predicate.field) && predicate.valid !== undefined;
}
export function isFieldPredicate(predicate) {
  return isFieldOneOfPredicate(predicate) || isFieldEqualPredicate(predicate) || isFieldRangePredicate(predicate) || isFieldLTPredicate(predicate) || isFieldGTPredicate(predicate) || isFieldLTEPredicate(predicate) || isFieldGTEPredicate(predicate);
}

function predicateValueExpr(v, timeUnit) {
  return valueExpr(v, {
    timeUnit: timeUnit,
    wrapTime: true
  });
}

function predicateValuesExpr(vals, timeUnit) {
  return vals.map(function (v) {
    return predicateValueExpr(v, timeUnit);
  });
} // This method is used by Voyager. Do not change its behavior without changing Voyager.


export function fieldFilterExpression(predicate) {
  var useInRange = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

  var _a;

  var field = predicate.field;
  var timeUnit = (_a = normalizeTimeUnit(predicate.timeUnit)) === null || _a === void 0 ? void 0 : _a.unit;
  var fieldExpr = timeUnit ? // For timeUnit, cast into integer with time() so we can use ===, inrange, indexOf to compare values directly.
  // TODO: We calculate timeUnit on the fly here. Consider if we would like to consolidate this with timeUnit pipeline
  // TODO: support utc
  "time(".concat(timeUnitFieldExpr(timeUnit, field), ")") : vgField(predicate, {
    expr: 'datum'
  });

  if (isFieldEqualPredicate(predicate)) {
    return "".concat(fieldExpr, "===").concat(predicateValueExpr(predicate.equal, timeUnit));
  } else if (isFieldLTPredicate(predicate)) {
    var upper = predicate.lt;
    return "".concat(fieldExpr, "<").concat(predicateValueExpr(upper, timeUnit));
  } else if (isFieldGTPredicate(predicate)) {
    var lower = predicate.gt;
    return "".concat(fieldExpr, ">").concat(predicateValueExpr(lower, timeUnit));
  } else if (isFieldLTEPredicate(predicate)) {
    var _upper = predicate.lte;
    return "".concat(fieldExpr, "<=").concat(predicateValueExpr(_upper, timeUnit));
  } else if (isFieldGTEPredicate(predicate)) {
    var _lower = predicate.gte;
    return "".concat(fieldExpr, ">=").concat(predicateValueExpr(_lower, timeUnit));
  } else if (isFieldOneOfPredicate(predicate)) {
    return "indexof([".concat(predicateValuesExpr(predicate.oneOf, timeUnit).join(','), "], ").concat(fieldExpr, ") !== -1");
  } else if (isFieldValidPredicate(predicate)) {
    return fieldValidPredicate(fieldExpr, predicate.valid);
  } else if (isFieldRangePredicate(predicate)) {
    var range = predicate.range;

    var _lower2 = isSignalRef(range) ? {
      signal: "".concat(range.signal, "[0]")
    } : range[0];

    var _upper2 = isSignalRef(range) ? {
      signal: "".concat(range.signal, "[1]")
    } : range[1];

    if (_lower2 !== null && _upper2 !== null && useInRange) {
      return 'inrange(' + fieldExpr + ', [' + predicateValueExpr(_lower2, timeUnit) + ', ' + predicateValueExpr(_upper2, timeUnit) + '])';
    }

    var exprs = [];

    if (_lower2 !== null) {
      exprs.push("".concat(fieldExpr, " >= ").concat(predicateValueExpr(_lower2, timeUnit)));
    }

    if (_upper2 !== null) {
      exprs.push("".concat(fieldExpr, " <= ").concat(predicateValueExpr(_upper2, timeUnit)));
    }

    return exprs.length > 0 ? exprs.join(' && ') : 'true';
  }
  /* istanbul ignore next: it should never reach here */


  throw new Error("Invalid field predicate: ".concat(stringify(predicate)));
}
export function fieldValidPredicate(fieldExpr) {
  var valid = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

  if (valid) {
    return "isValid(".concat(fieldExpr, ") && isFinite(+").concat(fieldExpr, ")");
  } else {
    return "!isValid(".concat(fieldExpr, ") || !isFinite(+").concat(fieldExpr, ")");
  }
}
export function normalizePredicate(f) {
  var _a;

  if (isFieldPredicate(f) && f.timeUnit) {
    return Object.assign(Object.assign({}, f), {
      timeUnit: (_a = normalizeTimeUnit(f.timeUnit)) === null || _a === void 0 ? void 0 : _a.unit
    });
  }

  return f;
}